import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useRef, useState } from 'react';
import { bulkClassification, type BulkJobStatus, type BulkProposal, type RequiredDocumentItem } from '@/api/endpoints';
import { Icon } from './Icon';
import { readDroppedFiles } from './dropFiles';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Bulk import katalogu z dokumentami do opracowania.
 * Flow:
 *  1. User upuszcza folder/pliki na strefę drop
 *  2. Frontend uploaduje wszystko, backend uruchamia klasyfikator (embedding + LLM hybrid)
 *  3. Polling co 2s — pokazujemy propozycje w miarę zakończenia
 *  4. User akceptuje / zmienia przypisanie / odrzuca każdą pozycję
 */
interface Props {
  open: boolean;
  studyId: number;
  /** Lista pozycji checklisty z karty (do dropdownu re-assign). */
  checklistItems: RequiredDocumentItem[];
  onClose: () => void;
  /** Wywoływane po akceptacji/odrzuceniu, żeby karta odświeżyła stan checklisty. */
  onChanged: () => void;
  /** Opcjonalne — gdy ustawione, modal startuje od razu w fazie review dla tego jobu. */
  resumeJobId?: string | null;
}

function fmtConfidence(c: number | null | undefined): string {
  if (c == null) return '—';
  return `${Math.round(c * 100)}%`;
}

function fmtElapsed(startIso: string | undefined): string {
  if (!startIso) return '';
  const ms = Date.now() - new Date(startIso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function phaseLabel(phase: BulkJobStatus['phase']): string {
  switch (phase) {
    case 'ingesting':   return 'Wgrywanie i ekstrakcja tekstu';
    case 'classifying': return 'Klasyfikacja AI';
    case 'cancelling':  return 'Anulowanie — czekam na zakończenie aktualnego pliku…';
    case 'done':        return 'Gotowe';
    case 'cancelled':   return 'Anulowane';
    case 'failed':      return 'Błąd';
    default:            return '';
  }
}

function confidenceClass(c: number | null | undefined): string {
  if (c == null) return 'text-ink-400';
  if (c >= 0.7) return 'text-green-700 font-semibold';
  if (c >= 0.5) return 'text-amber-700';
  return 'text-ink-500';
}

export function BulkClassificationModal({ open, studyId, checklistItems, onClose, onChanged, resumeJobId }: Props) {
  const [overrides, setOverrides] = useState<Record<number, number | 'KEEP' | 'NONE'>>({});
  const [phase, setPhase] = useState<'drop' | 'uploading' | 'review'>('drop');
  const [dragOver, setDragOver] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<BulkJobStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [skipNotice, setSkipNotice] = useState<string | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadInfo, setUploadInfo] = useState<{ filesCount: number; loadedKB: number; totalKB: number } | null>(null);
  const uploadAbortRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setOverrides({});
      setPhase('drop'); setJobId(null); setStatus(null); setErr(null); setSkipNotice(null);
      setUploadPercent(0); setUploadInfo(null);
      uploadAbortRef.current = null;
    } else if (resumeJobId) {
      // Modal otwarty z wskazaniem na istniejący job → pomiń drop/upload, wskocz w review.
      setJobId(resumeJobId);
      setPhase('review');
      setErr(null);
    }
  }, [open, resumeJobId]);

  useEffect(() => {
    if (!jobId || phase !== 'review') return;
    let mounted = true;
    const poll = async () => {
      try {
        const res = await bulkClassification.proposals(jobId);
        if (!mounted) return;
        // Po lokalnym ustawieniu phase='cancelling' nie cofamy do classifying jeśli serwer
        // jeszcze nie zauważył flagi (delay między kliknięciem cancel a zakończeniem
        // bieżącej klasyfikacji). Trzymamy 'cancelling' aż serwer potwierdzi 'cancelled'.
        setStatus(prev => {
          if (prev?.phase === 'cancelling' && (res.phase === 'ingesting' || res.phase === 'classifying')) {
            return { ...res, phase: 'cancelling' };
          }
          return res;
        });
        const stillWorking = res.phase === 'ingesting' || res.phase === 'classifying'
                          || res.phase === 'cancelling' || res.processing > 0;
        if (stillWorking) setTimeout(poll, 2000);
      } catch (e) {
        if (!mounted) return;
        setErr(e instanceof Error ? e.message : String(e));
      }
    };
    poll();
    return () => { mounted = false; };
  }, [jobId, phase]);

  // Live timer w fazie 'review' — odświeża się co sekundę aby pokazać upływający czas.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (phase !== 'review') return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: overrides, onClose: () => onClose(), open: open, busy: phase === 'uploading',
  });

  if (!open) return null;

  const startUpload = async (files: File[]) => {
    if (files.length === 0) return;
    const real = files.filter(f => f.size > 0 || f.type !== '');
    if (real.length === 0) {
      setErr('Brak realnych plików — przeciągnięty katalog jest pusty.');
      return;
    }
    const totalBytes = real.reduce((s, f) => s + f.size, 0);
    // Limit po stronie nginx + Spring (`max-request-size: 2GB`). Wyłapujemy klient-side
    // żeby user dostał czytelny komunikat zamiast HTTP 413 z gołego serwera.
    const MAX_TOTAL_BYTES = 2 * 1024 * 1024 * 1024;        // 2 GB
    const MAX_PER_FILE_BYTES = 250 * 1024 * 1024;          // 250 MB
    const fmtMb = (b: number) => (b / 1024 / 1024).toFixed(0) + ' MB';
    if (totalBytes > MAX_TOTAL_BYTES) {
      setErr(
        `Folder zawiera ${real.length} plików o łącznej wadze ${fmtMb(totalBytes)}. `
        + `Limit pojedynczego uploadu to 2 GB. Wgraj folder w mniejszych częściach `
        + `(np. po 500-1000 MB) — najprościej rozdziel po podfolderach.`
      );
      return;
    }
    const oversized = real.filter(f => f.size > MAX_PER_FILE_BYTES);
    if (oversized.length > 0) {
      const names = oversized.map(f => `${f.name} (${fmtMb(f.size)})`).slice(0, 3).join(', ');
      const more = oversized.length > 3 ? ` (+${oversized.length - 3} więcej)` : '';
      setErr(
        `${oversized.length} ${oversized.length === 1 ? 'plik przekracza' : 'plików przekracza'} `
        + `limit 250 MB na pojedynczy plik: ${names}${more}. Skompresuj/podziel je przed wgraniem.`
      );
      return;
    }
    setPhase('uploading'); setErr(null);
    setUploadPercent(0);
    setUploadInfo({ filesCount: real.length, loadedKB: 0, totalKB: Math.round(totalBytes / 1024) });
    const upload = bulkClassification.start(studyId, real, p => {
      setUploadPercent(p.percent);
      setUploadInfo({
        filesCount: real.length,
        loadedKB: Math.round(p.loaded / 1024),
        totalKB: Math.round(p.total / 1024),
      });
    });
    uploadAbortRef.current = upload.abort;
    try {
      const res = await upload.promise;
      // Pre-flight dedup po stronie serwera: część plików mogła zostać pominięta.
      if (res.skippedDuplicates > 0) {
        const sample = res.skippedFilenames.slice(0, 5).join(', ');
        const more = res.skippedFilenames.length > 5 ? ` (+${res.skippedFilenames.length - 5})` : '';
        setSkipNotice(`Pominięto ${res.skippedDuplicates} duplikat${res.skippedDuplicates === 1 ? '' : 'y'}: ${sample}${more}`);
      } else {
        setSkipNotice(null);
      }
      if (res.jobId == null) {
        // Wszystkie pliki to duplikaty — nie ma jobu do śledzenia.
        setErr(res.warning ?? 'Wszystkie pliki to duplikaty — nic nie wysłano.');
        setPhase('drop');
        return;
      }
      setJobId(res.jobId);
      setPhase('review');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase('drop');
    } finally {
      uploadAbortRef.current = null;
    }
  };

  const abortUpload = () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current();
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const collected = await readDroppedFiles(e.dataTransfer);
    startUpload(collected);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div className="bg-white rounded-lg shadow-2xl max-w-[1100px] w-full mt-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-ink-100 px-5 py-3 flex items-center gap-3">
          <Icon name="folder" className="w-4 h-4 text-water-600"/>
          <h2 className="text-[16px] font-semibold m-0">Bulk import katalogu (AI klasyfikacja)</h2>
          <button onClick={requestClose} className="ml-auto text-ink-400 hover:text-ink-700"><Icon name="x"/></button>
        </div>

        <div className="p-5">
          {err && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-[12px]">Błąd: {err}</div>}
          {skipNotice && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-[12px]">
              ⚠ {skipNotice}
            </div>
          )}
          {status && status.skippedDuplicates !== undefined && status.skippedDuplicates > 0 && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-[12px]">
              Server-side dedup: pominięto {status.skippedDuplicates} duplikat{status.skippedDuplicates === 1 ? '' : 'y'}
              {status.skippedFilenames && status.skippedFilenames.length > 0 && (
                <> — {status.skippedFilenames.slice(0, 5).join(', ')}
                  {status.skippedFilenames.length > 5 && ` (+${status.skippedFilenames.length - 5})`}
                </>
              )}
            </div>
          )}

          {phase === 'drop' && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition '
                + (dragOver ? 'border-water-500 bg-water-50' : 'border-ink-300 hover:border-water-400 hover:bg-ink-50')}>
              <div className="text-[14px] text-ink-700 font-semibold mb-2">
                Upuść tutaj cały katalog z plikami (lub pojedyncze pliki)
              </div>
              <div className="text-[12px] text-ink-500">
                Obsługiwane: PDF, DOCX, DOC, PNG, JPG, TXT. PDF z zakładkami zostaną automatycznie rozbite na sekcje.
              </div>
              <div className="text-[11px] text-ink-400 mt-2">
                AI przeanalizuje treść i zaproponuje przypisanie każdego pliku do pozycji checklisty.
              </div>
              <input ref={fileInputRef} type="file" hidden multiple
                     {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                     onChange={e => {
                       const fs = Array.from(e.target.files ?? []);
                       if (fs.length > 0) startUpload(fs);
                       e.target.value = '';
                     }}/>
            </div>
          )}

          {phase === 'uploading' && (
            <div className="py-8 px-4 text-center">
              <div className="text-[13px] text-ink-800 font-semibold mb-2">
                Wysyłanie plików{uploadInfo ? ` (${uploadInfo.filesCount})` : ''}…
              </div>
              <div className="w-full max-w-[600px] mx-auto bg-ink-100 rounded-full h-3 overflow-hidden">
                <div className="bg-water-500 h-full transition-all"
                     style={{ width: uploadPercent + '%' }}/>
              </div>
              <div className="mt-2 text-[12px] text-ink-600 font-mono">
                {uploadPercent}%
                {uploadInfo && (
                  <span className="ml-3 text-ink-500">
                    {uploadInfo.loadedKB.toLocaleString('pl-PL')} / {uploadInfo.totalKB.toLocaleString('pl-PL')} kB
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11px] text-ink-400">
                {uploadPercent < 100
                  ? 'Po zakończeniu wysyłki uruchomi się klasyfikator AI.'
                  : 'Wysłano. Trwa przetwarzanie po stronie serwera…'}
              </div>
              {uploadPercent < 100 && (
                <button onClick={abortUpload} className="btn btn-secondary btn-sm mt-3 text-red-600">
                  <Icon name="x" className="w-3 h-3"/> Anuluj wysyłkę
                </button>
              )}
            </div>
          )}

          {phase === 'review' && status && (
            <ReviewView
              status={status}
              overrides={overrides}
              setOverrides={setOverrides}
              checklistItems={checklistItems}
              onAccept={async (id, overrideSrdId) => {
                try {
                  await bulkClassification.accept(id, overrideSrdId);
                  setOverrides(previous => { const next = { ...previous }; delete next[id]; return next; });
                  const res = await bulkClassification.proposals(status.jobId);
                  setStatus(res);
                  onChanged();
                } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
              }}
              onReject={async id => {
                try {
                  await bulkClassification.reject(id);
                  setOverrides(previous => { const next = { ...previous }; delete next[id]; return next; });
                  const res = await bulkClassification.proposals(status.jobId);
                  setStatus(res);
                } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
              }}
              onCancelJob={async () => {
                if (!confirm('Anulować job? Pozostałe pliki zatrzymają się — wciąż będziesz mógł zatwierdzić/odrzucić te już sklasyfikowane.')) return;
                // Optimistic update: pokaż "Anulowanie…" od razu, zanim pierwsze polling
                // zobaczy zmianę (aktualny plik wciąż się klasyfikuje przez kilka sekund).
                setStatus(prev => prev ? { ...prev, phase: 'cancelling' } : prev);
                try { await bulkClassification.cancel(status.jobId); }
                catch (e) {
                  setErr(e instanceof Error ? e.message : String(e));
                  // Cofnij optymistyczne ustawienie gdy POST nie przeszedł
                  setStatus(prev => prev && prev.phase === 'cancelling' ? { ...prev, phase: status.phase } : prev);
                }
              }}
              onReclassify={async (all: boolean) => {
                try {
                  const r = await bulkClassification.reclassify(status.jobId, all);
                  if (r.queued > 0) {
                    const res = await bulkClassification.proposals(status.jobId);
                    setStatus(res);
                  } else {
                    alert('Brak propozycji do re-klasyfikacji.');
                  }
                } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
              }}
              onReclassifyOneLlm={async id => {
                try {
                  await bulkClassification.reclassifyOneViaLlm(id);
                  const res = await bulkClassification.proposals(status.jobId);
                  setStatus(res);
                } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewView({ status, overrides, setOverrides, checklistItems, onAccept, onReject, onCancelJob, onReclassify, onReclassifyOneLlm }: {
  status: BulkJobStatus;
  overrides: Record<number, number | 'KEEP' | 'NONE'>;
  setOverrides: React.Dispatch<React.SetStateAction<Record<number, number | 'KEEP' | 'NONE'>>>;
  checklistItems: RequiredDocumentItem[];
  onAccept: (id: number, overrideSrdId: number | null) => void;
  onReject: (id: number) => void;
  onCancelJob: () => void;
  onReclassify: (all: boolean) => void;
  onReclassifyOneLlm: (id: number) => Promise<void>;
}) {
  const [llmBusyId, setLlmBusyId] = useState<number | null>(null);

  const setOverride = (proposalId: number, value: number | 'KEEP' | 'NONE') => {
    setOverrides(prev => {
      const next = { ...prev };
      if (value === 'KEEP') delete next[proposalId];
      else next[proposalId] = value;
      return next;
    });
  };

  const resolveTarget = (p: BulkProposal): number | null => {
    const o = overrides[p.id];
    if (o === 'NONE') return null;
    if (o === 'KEEP' || o === undefined) return p.proposedSrdId;
    return o as number;
  };

  const elapsed = fmtElapsed(status.startedAt);
  const isIngesting = status.phase === 'ingesting';
  const isClassifying = status.phase === 'classifying';
  const isCancelling = status.phase === 'cancelling';
  const ingestPct = status.totalFiles && status.totalFiles > 0
    ? Math.min(100, Math.round(((status.ingested ?? 0) / status.totalFiles) * 100))
    : 0;
  const classifyTotal = status.total;
  const classifyDone = classifyTotal - status.processing;
  const classifyPct = classifyTotal > 0
    ? Math.min(100, Math.round((classifyDone / classifyTotal) * 100))
    : 0;

  return (
    <div>
      {/* Phase progress bar — widoczny dopóki serwer pracuje */}
      {(isIngesting || isClassifying || isCancelling) && (
        <div className={'mb-3 p-3 border rounded-md ' +
                         (isCancelling ? 'border-amber-200 bg-amber-50/60' : 'border-ink-100 bg-ink-50/40')}>
          <div className="flex items-center gap-2 text-[12px] mb-1.5">
            <span className={'inline-block w-3 h-3 border-2 border-t-transparent rounded-full animate-spin ' +
                              (isCancelling ? 'border-amber-600' : 'border-water-600')}/>
            <span className={'font-semibold ' + (isCancelling ? 'text-amber-800' : 'text-ink-800')}>
              {phaseLabel(status.phase)}
            </span>
            {!isCancelling && isIngesting && (
              <span className="text-ink-600 font-mono">
                {status.ingested ?? 0} / {status.totalFiles ?? '?'} plików
              </span>
            )}
            {!isCancelling && isClassifying && (
              <span className="text-ink-600 font-mono">
                {classifyDone} / {classifyTotal} plików
              </span>
            )}
            {elapsed && (
              <span className="ml-auto text-ink-500 font-mono">⏱ {elapsed}</span>
            )}
          </div>
          {!isCancelling && (
            <div className="w-full bg-ink-100 rounded-full h-2 overflow-hidden">
              <div className="bg-water-500 h-full transition-all"
                   style={{ width: (isIngesting ? ingestPct : classifyPct) + '%' }}/>
            </div>
          )}
          {isIngesting && status.currentFilename && (
            <div className="mt-1.5 text-[11px] text-ink-500 truncate" title={status.currentFilename}>
              Aktualnie: <span className="font-mono">{status.currentFilename}</span>
            </div>
          )}
          {isIngesting && (
            <div className="mt-1 text-[11px] text-ink-400">
              Ekstrakcja tekstu / OCR z każdego pliku — operacja może trwać kilka minut przy dużych katalogach.
            </div>
          )}
          {isCancelling && (
            <div className="mt-1 text-[11px] text-amber-700">
              Aktualnie przetwarzany plik kończy bieżącą klasyfikację (LLM-call jest blokujący).
              Po nim pętla wyjdzie i pozostałe pliki zostaną oznaczone jako anulowane.
            </div>
          )}
        </div>
      )}
      {status.phase === 'failed' && status.error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-[12px]">
          Job zakończony błędem: {status.error}
        </div>
      )}

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="text-[12px] text-ink-700 flex-1 min-w-0">
          Job <span className="font-mono">{status.jobId}</span> · {status.total} plików
          {!isIngesting && !isClassifying && elapsed && (
            <span className="ml-2 text-ink-500 font-mono">⏱ {elapsed}</span>
          )}
          {status.processing > 0 && !isClassifying && (
            <span className="ml-2 text-water-600">
              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1 align-middle"/>
              {status.processing} klasyfikuje
            </span>
          )}
          {status.proposed > 0 && <span className="ml-2 text-amber-700">{status.proposed} czeka na decyzję</span>}
          {status.done > 0 && <span className="ml-2 text-green-700">{status.done} obsłużonych</span>}
        </div>
        <div className="flex gap-2">
          {status.processing > 0 && !isCancelling && (
            <button onClick={onCancelJob} className="btn btn-secondary btn-sm text-red-600"
                    title="Zatrzymaj klasyfikację — już sklasyfikowane pliki pozostaną do zatwierdzenia">
              <Icon name="x" className="w-3 h-3"/> Anuluj job
            </button>
          )}
          {isCancelling && (
            <button disabled className="btn btn-secondary btn-sm text-amber-700 opacity-70 cursor-wait">
              <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1"/>
              Anulowanie…
            </button>
          )}
          {status.processing === 0 && !isCancelling && (
            <>
              <button onClick={() => onReclassify(false)}
                      className="btn btn-secondary btn-sm"
                      title="Re-klasyfikuje tylko pliki bez wyraźnego dopasowania (luźne). Pomocne gdy admin dodał nowe pozycje słownika.">
                <Icon name="sparkles" className="w-3 h-3"/> Przeklasyfikuj luźne
              </button>
              <button onClick={() => onReclassify(true)}
                      className="btn btn-secondary btn-sm"
                      title="Re-klasyfikuje wszystkie nieobsłużone propozycje (PROPOSED). ACCEPTED i REJECTED zostają.">
                <Icon name="sparkles" className="w-3 h-3"/> Przeklasyfikuj wszystkie
              </button>
            </>
          )}
        </div>
      </div>

      <table className="table-hw">
        <thead>
          <tr>
            <th>Plik</th>
            <th className="w-72">Sugerowana pozycja</th>
            <th className="w-20">Pewność</th>
            <th className="w-24">Źródło</th>
            <th className="w-44">Akcje</th>
          </tr>
        </thead>
        <tbody>
          {status.items.map(p => {
            const isProcessing = p.status === 'PROCESSING';
            const isResolved = p.status === 'ACCEPTED' || p.status === 'REJECTED';
            return (
              <tr key={p.id} className={isResolved ? 'opacity-50' : ''}>
                <td>
                  <div className="text-[13px] text-ink-900 truncate max-w-[320px]" title={p.archiveDocumentName}>
                    {p.archiveDocumentName}
                  </div>
                  {p.archiveSizeBytes != null && (
                    <div className="text-[10px] text-ink-400 font-mono">
                      {Math.round(p.archiveSizeBytes / 1024)} kB
                    </div>
                  )}
                  {p.explanation && (
                    <div className="text-[11px] text-ink-500 italic mt-0.5">💬 {p.explanation}</div>
                  )}
                </td>
                <td>
                  {isProcessing ? (
                    <span className="text-[11px] text-water-600 flex items-center gap-1">
                      <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                      analiza…
                    </span>
                  ) : (
                    <SearchableSelect
                      value={String(overrides[p.id] ?? 'KEEP')}
                      disabled={isResolved}
                      onChange={v => {
                        if (v === 'KEEP') setOverride(p.id, 'KEEP');
                        else if (v === 'NONE') setOverride(p.id, 'NONE');
                        else setOverride(p.id, Number(v));
                      }}
                      className="select-sm text-[12px]">
                      <option value="KEEP">
                        {p.proposedSrdId
                          ? `▶ ${p.proposedSrdCode} — ${p.proposedSrdName}`
                          : '▶ Luźny plik (bez przypisania)'}
                      </option>
                      <option value="NONE">— Luźny plik (zostaw w archiwum) —</option>
                      <optgroup label="Przypisz do pozycji checklisty">
                        {checklistItems.map(it => (
                          <option key={it.id} value={it.id}>
                            {it.code} — {it.name}
                          </option>
                        ))}
                      </optgroup>
                    </SearchableSelect>
                  )}
                </td>
                <td className={'font-mono text-[12px] ' + confidenceClass(p.confidence != null ? Number(p.confidence) : null)}>
                  {fmtConfidence(p.confidence != null ? Number(p.confidence) : null)}
                </td>
                <td className="text-[11px] text-ink-500 font-mono">{p.source ?? '—'}</td>
                <td>
                  {isResolved ? (
                    <span className={'pill ' + (p.status === 'ACCEPTED' ? 'pill-green' : 'pill-amber')}>
                      {p.status === 'ACCEPTED' ? '✓ zatwierdzono' : '× odrzucono'}
                    </span>
                  ) : isProcessing ? (
                    <span className="text-ink-400 text-[11px]">…</span>
                  ) : (
                    <div className="flex gap-1 items-center">
                      {p.source === 'EMBEDDING' && (
                        <button
                          onClick={async () => {
                            setLlmBusyId(p.id);
                            try { await onReclassifyOneLlm(p.id); }
                            finally { setLlmBusyId(null); }
                          }}
                          disabled={llmBusyId === p.id}
                          className="btn btn-secondary btn-sm text-amber-700"
                          title="Reklasyfikuj przez LLM — wymusza ponowną klasyfikację gdy dopasowanie semantyczne było słabe (~10-30s)">
                          {llmBusyId === p.id
                            ? <span className="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                            : <Icon name="sparkles" className="w-3 h-3"/>}
                        </button>
                      )}
                      <button
                        onClick={() => onAccept(p.id, resolveTarget(p))}
                        disabled={llmBusyId === p.id}
                        className="btn btn-primary btn-sm"
                        title="Zatwierdź wybór i podepnij plik">
                        <Icon name="check" className="w-3 h-3"/> Zatwierdź
                      </button>
                      <button
                        onClick={() => onReject(p.id)}
                        disabled={llmBusyId === p.id}
                        className="btn btn-secondary btn-sm text-red-600"
                        title="Odrzuć — plik zostanie w archiwum, ale nie zostanie podpięty">
                        <Icon name="x" className="w-3 h-3"/>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
